import { useBlocker } from '@tanstack/react-router'
import { type ReactNode, useRef } from 'react'
import * as m from '~/paraglide/messages'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/shared/ui/alert-dialog'

export function useLeaveGuard(
  active: boolean,
  body: string,
): { dialog: ReactNode; release: () => void } {
  const activeRef = useRef(active)
  activeRef.current = active
  const released = useRef(false)
  const guarding = () => activeRef.current && !released.current

  const blocker = useBlocker({
    shouldBlockFn: ({ current, next }) => guarding() && current.pathname !== next.pathname,
    enableBeforeUnload: guarding,
    withResolver: true,
  })

  const dialog = (
    <AlertDialog
      open={blocker.status === 'blocked'}
      onOpenChange={(open) => {
        if (!open) blocker.reset?.()
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{m.leaveTitle()}</AlertDialogTitle>
          <AlertDialogDescription>{body}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => blocker.reset?.()}>{m.leaveStay()}</AlertDialogCancel>
          <AlertDialogAction onClick={() => blocker.proceed?.()}>{m.leaveGo()}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )

  return {
    dialog,
    release: () => {
      released.current = true
    },
  }
}
